import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";

const typographyVariants = cva("qodo qodo-typography", {
  variants: {
    variant: {
      h1: "scroll-m-20 text-4xl font-normal tracking-tight lg:text-5xl",
      h2: "scroll-m-20 text-3xl font-normal tracking-tight first:mt-0",
      h3: "scroll-m-20 text-2xl font-normal tracking-tight",
      h4: "scroll-m-20 text-xl font-medium tracking-tight",
      h5: "scroll-m-20 text-[17px] font-normal tracking-tight",
      h6: "scroll-m-20 text-base font-normal leading-[140%] tracking-tight",
      p: "leading-7 [&:not(:first-child)]:mt-6",
      lead: "text-xl text-muted-foreground",
      large: "text-lg font-semibold",
      small: "text-sm font-medium leading-none",
      muted: "text-sm text-muted-foreground",
      body: "text-base leading-relaxed",
      "mono-body": "font-mono text-mono-body",
      "mono-small": "font-mono text-mono-small",
      "body-strong": "text-sm font-semibold leading-none",
      s: "font-normal text-xs leading-[150%] tracking-[0.12px]",
      xs: "text-[10px] font-normal leading-[150%] tracking-[0.2px]",
      caption: "text-xs text-muted-foreground",
      code: "font-mono relative rounded bg-fill-input-neutral-idle px-[0.3rem] py-[0.2rem] text-sm font-semibold",
      "code-inline":
        "font-mono relative rounded bg-fill-input-neutral-idle px-[0.3rem] py-[0.2rem] text-xs",
      "code-block":
        "font-mono block w-full rounded-lg bg-fill-input-neutral-idle p-4 text-sm leading-relaxed overflow-x-auto",
      blockquote: "mt-6 border-l-2 pl-6 italic text-muted-foreground",
      list: "my-6 ml-6 list-disc [&>li]:mt-2",
      "list-ordered": "my-6 ml-6 list-decimal [&>li]:mt-2",
    },
  },
  defaultVariants: {
    variant: "p",
  },
});

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "p"
    | "span"
    | "div"
    | "blockquote"
    | "ul"
    | "ol"
    | "code"
    | "pre";
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, ...props }, ref) => {
    const Component = as || getDefaultElement(variant) || "p";

    return React.createElement(Component, {
      className: cn(typographyVariants({ variant, className })),
      ref,
      ...props,
    });
  },
);

Typography.displayName = "Typography";

function getDefaultElement(
  variant: TypographyProps["variant"],
): TypographyProps["as"] {
  switch (variant) {
    case "list":
      return "ul";
    case "list-ordered":
      return "ol";
    case "code-inline":
      return "code";
    case "code-block":
      return "pre";
    case "lead":
    case "large":
    case "small":
    case "muted":
    case "body":
    case "body-strong":
    case "s":
    case "xs":
    case "mono-body":
    case "mono-small":
    case "caption":
      return "p";
    default:
      return variant || "p";
  }
}

export { Typography, typographyVariants };
